import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  FEEDBACK_COMMENT_MAX_LENGTH,
  FEEDBACK_MAX_CATEGORIES,
  NEGATIVE_FEEDBACK_CATEGORIES,
  POSITIVE_FEEDBACK_CATEGORIES,
  type FeedbackPromptMode,
} from '@bizzdesign/ai-assistant/shared/models';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';

export type FeedbackFormDirection = 'up' | 'down';
export type FeedbackRating = 1 | 2 | 3 | 4 | 5;

export interface FeedbackFormSubmission {
  readonly comment: string;
  readonly categories: ReadonlyArray<string>;
  readonly direction: FeedbackFormDirection;
  readonly rating?: FeedbackRating;
}

interface StarState {
  readonly value: FeedbackRating;
  readonly filled: boolean;
  readonly selected: boolean;
}

interface ChipState {
  readonly value: string;
  readonly selected: boolean;
  readonly labelKey: string;
}

const STAR_VALUES: ReadonlyArray<FeedbackRating> = [1, 2, 3, 4, 5];

/** Ratings at or above this threshold count as a positive experience. */
const POSITIVE_RATING_THRESHOLD: FeedbackRating = 4;

const CATEGORY_I18N_KEY_BY_CATEGORY: Record<string, string> = {
  'incorrect-info': 'categoryIncorrectInfo',
  irrelevant: 'categoryIrrelevant',
  incomplete: 'categoryIncomplete',
  'wrong-tool-call': 'categoryWrongToolCall',
  'hallucinated-entities': 'categoryHallucinatedEntities',
  formatting: 'categoryFormatting',
  'too-slow': 'categoryTooSlow',
  accurate: 'categoryAccurate',
  'helpful-action': 'categoryHelpfulAction',
  'well-structured': 'categoryWellStructured',
  fast: 'categoryFast',
  other: 'categoryOther',
};

@Component({
  selector: 'bd-assistant-feedback-form',
  standalone: true,
  imports: [FormsModule, ButtonModule, TextareaModule, SharedI18nModule],
  templateUrl: './assistant-feedback-form.component.html',
  styleUrl: './assistant-feedback-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantFeedbackFormComponent {
  comment = model<string>('');
  mode = input<FeedbackPromptMode>('voluntary');
  direction = input<FeedbackFormDirection>('down');
  /**
   * When true, the form renders its own 5-star rating picker at the top.
   * Pills and the comment box stay hidden until the user picks a rating,
   * and Submit requires a rating. Used by the soft prompt card so users
   * can express the full positive-to-negative spectrum and pills/comment
   * become an optional second step rather than a mandatory one.
   */
  interactive = input<boolean>(false);
  showSkip = input<boolean>(false);

  submitted = output<FeedbackFormSubmission>();
  dismissed = output<void>();
  skipped = output<void>();

  protected readonly maxLength = FEEDBACK_COMMENT_MAX_LENGTH;
  protected readonly maxCategories = FEEDBACK_MAX_CATEGORIES;

  private readonly pickedRating = signal<FeedbackRating | null>(null);

  protected readonly hasRating = computed(() => this.pickedRating() !== null);

  protected readonly effectiveDirection = computed<FeedbackFormDirection>(
    () => {
      if (this.interactive()) {
        const rating = this.pickedRating();
        if (rating === null) return 'down';
        return rating >= POSITIVE_RATING_THRESHOLD ? 'up' : 'down';
      }
      return this.direction();
    },
  );

  protected readonly selectedCategories = signal<ReadonlySet<string>>(
    new Set(),
  );

  constructor() {
    // Clear chip selections whenever the effective direction changes —
    // a positive chip becomes meaningless if the user re-rates lower
    // (and vice versa).
    effect(() => {
      this.effectiveDirection();
      untracked(() => this.selectedCategories.set(new Set()));
    });
  }

  protected readonly starStates = computed<ReadonlyArray<StarState>>(() => {
    const current = this.pickedRating();
    return STAR_VALUES.map((value) => ({
      value,
      filled: current !== null && value <= current,
      selected: current === value,
    }));
  });

  protected readonly availableCategories = computed<ReadonlyArray<string>>(
    () => {
      // Voluntary mode keeps the legacy "comment only" UX — no chip taxonomy.
      if (this.mode() === 'voluntary' && !this.interactive()) {
        return [];
      }
      return this.effectiveDirection() === 'up'
        ? POSITIVE_FEEDBACK_CATEGORIES
        : NEGATIVE_FEEDBACK_CATEGORIES;
    },
  );

  protected readonly chipStates = computed<ReadonlyArray<ChipState>>(() => {
    const selected = this.selectedCategories();
    return this.availableCategories().map((value) => ({
      value,
      selected: selected.has(value),
      labelKey: CATEGORY_I18N_KEY_BY_CATEGORY[value] ?? 'categoryOther',
    }));
  });

  protected readonly canSubmit = computed(() => {
    if (this.interactive()) return this.pickedRating() !== null;
    return true;
  });

  protected pickRating(rating: FeedbackRating): void {
    this.pickedRating.set(rating);
  }

  protected toggleCategory(category: string): void {
    this.selectedCategories.update((current) => {
      const next = new Set(current);
      if (next.has(category)) {
        next.delete(category);
        return next;
      }
      if (next.size >= this.maxCategories) {
        return current;
      }
      next.add(category);
      return next;
    });
  }

  onSubmit(): void {
    if (!this.canSubmit()) return;
    const rating = this.pickedRating();
    this.submitted.emit({
      comment: this.comment().trim(),
      categories: Array.from(this.selectedCategories()),
      direction: this.effectiveDirection(),
      ...(rating === null ? {} : { rating }),
    });
  }

  onDismiss(): void {
    this.dismissed.emit();
  }

  /**
   * In interactive mode the rating is the mandatory signal — if the user
   * has already picked a rating, "Skip" still submits it (pills + comment
   * are treated as optional follow-up the user opted out of). Only a
   * skip with no rating is treated as a true dismissal.
   */
  onSkip(): void {
    if (this.interactive() && this.pickedRating() !== null) {
      this.onSubmit();
      return;
    }
    this.skipped.emit();
  }
}
